import React, { useEffect } from 'react';
import { GoalBase, GoalViewBy } from '../types';
import { GoalFilter, GoalsFilter } from './GoalsFilter';
import SkeletonCard from '../../../../components/Skeleton/Card';
import { Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useGoalFilterOptions } from '../useGoalSelectOptions';
import { GoalType } from '../../../../__generated__/graphql';
import { useGoalsContext } from '../GoalsContext/GoalsContext';
import { GoalWeightValidation } from '../useGoalWeightValidation';

const i18prefix = 'performanceReview.goals2';

type Props = {
  filter: GoalFilter;
  onChange: (filter: GoalFilter) => void;
  goals: GoalBase[] | undefined;
  isReviewGoals?: boolean;
  viewBy: GoalViewBy;
  onViewByChange: (viewBy: GoalViewBy) => void;
  goalWeightValidation: GoalWeightValidation;
};

export const GoalsFilterConnected: React.FC<Props> = ({
  goals,
  filter,
  onChange,
  isReviewGoals,
  viewBy,
  onViewByChange,
  goalWeightValidation,
}) => {
  const { t } = useTranslation();
  const {
    currentPeriodGoalCategories,
    isManagerView,
    employeeAssignedCategories,
  } = useGoalsContext();

  const effectiveCategories = isManagerView
    ? currentPeriodGoalCategories
    : employeeAssignedCategories.map((a) => a.category);

  const { options, loading, error } = useGoalFilterOptions(
    goals,
    effectiveCategories,
  );

  // Drop filter by employeeId if we do not have that employee in reports
  useEffect(() => {
    if (
      options &&
      filter.employeeId &&
      filter.filterType === 'myReportsGoals' &&
      options.managerReports.every((x) => x.value !== filter.employeeId)
    ) {
      onChange({ ...filter, employeeId: null });
    }
  }, [options, filter, onChange]);

  // Drop filter by employeeId if we do not have that employee in collegues
  useEffect(() => {
    if (
      options &&
      filter.employeeId &&
      filter.filterType === 'colleaguesGoals' &&
      options.colleagues.every((x) => x.value !== filter.employeeId)
    ) {
      onChange({ ...filter, employeeId: null });
    }
  }, [options, filter, onChange]);

  if (error) {
    return (
      <Card id="filter-goals-card">
        <Card.Body>{t(`${i18prefix}.goalsFilter.errorLoading`)}</Card.Body>
      </Card>
    );
  }

  if (!goals || loading || !options) {
    return <SkeletonCard height={210} />;
  }

  const getEmployeeOptions = () => {
    switch (filter.filterType) {
      case 'myReportsGoals':
        return options.managerReports;
      case 'colleaguesGoals':
        return options.colleagues;
      default:
        return options.employees;
    }
  };

  const getGoalTypeOptions = () => {
    switch (filter.filterType) {
      case 'myReportsGoals':
      case 'myGoals':
      case 'colleaguesGoals':
        return options.goalTypes.filter(
          (goalType) => goalType.value !== GoalType.Company,
        );
      default:
        return options.goalTypes;
    }
  };

  return (
    <GoalsFilter
      tagsOptions={options.tags}
      periodsOptions={options.periods}
      employeeOptions={getEmployeeOptions()}
      goalTypeOptions={getGoalTypeOptions()}
      categoryOptions={options.goalCategories}
      statusOptions={options.statuses}
      goals={goals}
      onClearFilter={() =>
        onChange({
          ...filter,
          tagId: null,
          periodId: null,
          employeeId: null,
          categoryId: null,
          status: null,
        })
      }
      onChange={onChange}
      filter={filter}
      showEmployeeFilter={filter.filterType !== 'myGoals' && !isReviewGoals}
      viewBy={viewBy}
      onViewByChange={onViewByChange}
      goalWeightValidation={goalWeightValidation}
    />
  );
};
